/**
 * BMI calculator (sections/hw-bmi-calculator.liquid).
 *
 * No submit button by design: on a phone a "Calculate" button sits below the
 * fold behind the keyboard, so the result updates live as you type and stays
 * directly under the fields.
 *
 * Metric and imperial are separate field sets rather than converted in place —
 * converting mid-entry produces the "72.5 kg becomes 11 st 6.1 lb" jitter that
 * makes people distrust the tool.
 */
(function () {
  var BANDS = [
    {
      id: 'under',
      max: 18.5,
      label: 'Below healthy range',
      meaning:
        'A BMI under 18.5 is below the healthy range. Being underweight can affect energy, bone health and immunity, so it is worth talking to your GP or a pharmacist about what is behind it.',
    },
    {
      id: 'healthy',
      max: 25,
      label: 'Healthy range',
      meaning:
        'Your BMI is in the healthy range for adults. Staying there is mostly about the everyday things — what you eat, how much you move, sleep and stress.',
    },
    {
      id: 'over',
      max: 30,
      label: 'Above healthy range',
      meaning:
        'A BMI of 25 to 29.9 is above the healthy range. Diet and activity changes are the usual first step. A doctor may consider prescription weight-loss treatment at this BMI if you also have a weight-related condition such as type 2 diabetes, high blood pressure or sleep apnoea.',
    },
    {
      id: 'obese',
      max: Infinity,
      label: 'Obesity range',
      meaning:
        'A BMI of 30 or over is classed as obesity. Prescription weight-loss treatment is usually considered from this point alongside diet and activity changes — an online doctor or your GP can tell you whether it is suitable for you.',
    },
  ];

  // The scale bar runs 15–40; keep the marker maths and the CSS band widths
  // (14% / 26% / 20% / 40%) reading from the same two numbers.
  var SCALE_MIN = 15;
  var SCALE_MAX = 40;

  var LIMITS = { kg: [20, 400], cm: [100, 250] };

  function num(el) {
    if (!el) return 0;
    var v = parseFloat(el.value);
    return isFinite(v) && v > 0 ? v : 0;
  }

  function band(bmi) {
    for (var i = 0; i < BANDS.length; i++) {
      if (bmi < BANDS[i].max) return BANDS[i];
    }
    return BANDS[BANDS.length - 1];
  }

  function init(root) {
    var els = {
      units: root.querySelectorAll('[data-hw-bmi-unit]'),
      sets: root.querySelectorAll('[data-hw-bmi-fields]'),
      hint: root.querySelector('[data-hw-bmi-hint]'),
      result: root.querySelector('[data-hw-bmi-result]'),
      value: root.querySelector('[data-hw-bmi-value]'),
      chip: root.querySelector('[data-hw-bmi-chip]'),
      marker: root.querySelector('[data-hw-bmi-marker]'),
      meaning: root.querySelector('[data-hw-bmi-meaning]'),
      kg: root.querySelector('[data-hw-bmi-kg]'),
      cm: root.querySelector('[data-hw-bmi-cm]'),
      st: root.querySelector('[data-hw-bmi-st]'),
      lb: root.querySelector('[data-hw-bmi-lb]'),
      ft: root.querySelector('[data-hw-bmi-ft]'),
      inch: root.querySelector('[data-hw-bmi-in]'),
    };

    var mode = 'metric';
    var defaultHint = els.hint ? els.hint.textContent : '';

    function measurements() {
      if (mode === 'metric') {
        return { kg: num(els.kg), cm: num(els.cm) };
      }
      var stone = num(els.st);
      var pounds = num(els.lb);
      var feet = num(els.ft);
      var inches = num(els.inch);
      return {
        kg: stone * 6.35029318 + pounds * 0.45359237,
        cm: feet * 30.48 + inches * 2.54,
        // A weight of 0 st 8 lb is valid, so completeness is "either box filled".
        partial: (stone || pounds) && (feet || inches) ? false : true,
      };
    }

    function showHint(text, isError) {
      if (!els.hint) return;
      els.hint.textContent = text;
      els.hint.classList.toggle('hw-bmi__hint--error', !!isError);
      els.hint.hidden = false;
    }

    function update() {
      var m = measurements();
      var incomplete = mode === 'metric' ? !m.kg || !m.cm : m.partial;

      if (incomplete) {
        if (els.result) els.result.hidden = true;
        showHint(defaultHint, false);
        return;
      }

      if (m.kg < LIMITS.kg[0] || m.kg > LIMITS.kg[1] || m.cm < LIMITS.cm[0] || m.cm > LIMITS.cm[1]) {
        if (els.result) els.result.hidden = true;
        showHint('Please check those figures — that height or weight is outside the range this tool covers.', true);
        return;
      }

      var metres = m.cm / 100;
      var bmi = m.kg / (metres * metres);
      var rounded = bmi.toFixed(1);
      var b = band(bmi);
      var pct = ((bmi - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;
      pct = Math.max(1.5, Math.min(98.5, pct));

      if (els.value) els.value.textContent = rounded;
      if (els.chip) {
        els.chip.textContent = b.label;
        els.chip.setAttribute('data-band', b.id);
      }
      if (els.marker) {
        els.marker.style.left = pct + '%';
        els.marker.setAttribute('aria-valuenow', rounded);
      }
      if (els.meaning) els.meaning.textContent = b.meaning;
      if (els.hint) els.hint.hidden = true;
      if (els.result) els.result.hidden = false;
    }

    Array.prototype.forEach.call(els.units, function (btn) {
      btn.addEventListener('click', function () {
        mode = btn.getAttribute('data-hw-bmi-unit');

        Array.prototype.forEach.call(els.units, function (other) {
          other.setAttribute('aria-pressed', String(other === btn));
        });
        Array.prototype.forEach.call(els.sets, function (set) {
          set.hidden = set.getAttribute('data-hw-bmi-fields') !== mode;
        });

        update();
      });
    });

    root.addEventListener('input', update);
    update();
  }

  function boot() {
    document.querySelectorAll('[data-hw-bmi]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
